import _ from 'lodash';
import multiMixin from '../../../privates/utils/multiMixin';
import * as members from '../../../privates/privateMembers';
import BackgroundColorMixin from './BackgroundColorMixin';
import BorderWidthMixin from './BorderWidthMixin';
import BorderColorMixin from './BorderColorMixin';
import BorderRadiusMixin from './BorderRadiusMixin';
import TextColorMixin from './TextColorMixin';
import monitoring from '../../../privates/monitoring';
import reporter from '../../../privates/reporter';
import {generateError} from '../../../privates/validations/errors';
import ForegroundColorMixin from './ForegroundColorMixin';

const SUPPORTED_STYLES = {
    Button: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    Box: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype],
    Checkbox: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype],
    UploadButton: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    TextInput: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    TextBox: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    Shape: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype],
    RadioButtonGroup: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    CheckboxGroup: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    Header: [BackgroundColorMixin.prototype, BorderColorMixin.prototype],
    Footer: [BackgroundColorMixin.prototype, BorderColorMixin.prototype],
    Dropdown: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, BorderRadiusMixin.prototype, TextColorMixin.prototype],
    ProgressBar: [BackgroundColorMixin.prototype, BorderColorMixin.prototype, BorderWidthMixin.prototype, ForegroundColorMixin.prototype]
};

function getRavenContextForStyle(){
    const RMI = members._RMI.get(this);
    const compId = members._compId.get(this);
    const type = members._type.get(this);
    const compNickname = members._compName.get(this);

    return {compId, type, compNickname, RMI};
}

function monitorCompStyleMixins(styleMixins) {
/**
     * @class Style
     * @memberOf $w
     * @summary An object representing an element's styles.
     * @description
     * Elements that expose a style property can be styled programmatically.
     * You cannot programmatically style elements that do not expose a style property.
     *
     * The following elements expose the style property:
     *
     *  + [`Box`]($w.Box.html)
     *  + [`Button`]($w.Button.html)
     *  + [`Checkbox`]($w.Checkbox.html)
     *  + [`CheckboxGroup`]($w.CheckboxGroup.html)
     *  + [`Dropdown`]($w.Dropdown.html)
     *  + [`Footer`]($w.Footer.html)
     *  + [`Header`]($w.Header.html)
     *  + [`ProgressBar`]($w.ProgressBar.html)
     *  + [`QuickActionBar`]($w.QuickActionBar.html)
     *  + [`RadioButtonGroup`]($w.RadioButtonGroup.html)
     *  + [`TextBox`]($w.TextBox.html)
     *  + [`TextInput`]($w.TextInput.html)
     *  + [`UploadButton`]($w.UploadButton.html)
     */

    class Style {
        constructor(RMI, compId, type, compNickname) {
            members._RMI.set(this, RMI);
            members._compId.set(this, compId);
            members._type.set(this, type);
            members._compName.set(this, compNickname);
        }
    }

    multiMixin(Style.prototype, styleMixins);
    return monitoring.monitorClass(Style, getRavenContextForStyle);
}

const styleClasses = _.transform(SUPPORTED_STYLES, (result, styleMixins, compType) => {
    result[compType] = monitorCompStyleMixins(styleMixins);
}, {});

export const extendedStyleClasses = {};

export const registerStyleMixinComponent = (compType, styleMixins) => {
    if (styleClasses[compType] || extendedStyleClasses[compType]) {
        reporter.reportSdkError(generateError.error_compStyleMixin_already_exists({type: compType}));
        return;
    }
    extendedStyleClasses[compType] = monitorCompStyleMixins(styleMixins.map((mixin) => mixin.prototype));
};

/**
 * @class StyleMixin
 * @summary Provides functionality for elements that can be styled.
 * @classdesc
 *  Elements that expose a [`style`](#style) property can be styled programmatically.
 *  You cannot programmatically style elements that do not expose a [`style`](#style) property.
 *
 *  The following elements expose the [`style`](#style) property:
 *
 *  + [`Box`]($w.Box.html)
 *  + [`Button`]($w.Button.html)
 *  + [`Checkbox`]($w.Checkbox.html)
 *  + [`CheckboxGroup`]($w.CheckboxGroup.html)
 *  + [`Dropdown`]($w.Dropdown.html)
 *  + [`Footer`]($w.Footer.html)
 *  + [`Header`]($w.Header.html)
 *  + [`ProgressBar`]($w.ProgressBar.html)
 *  + [`QuickActionBar`]($w.QuickActionBar.html)
 *  + [`RadioButtonGroup`]($w.RadioButtonGroup.html)
 *  + [`TextBox`]($w.TextBox.html)
 *  + [`TextInput`]($w.TextInput.html)
 *  + [`UploadButton`]($w.UploadButton.html)
 *
 * @memberof $w
 * @tagname styleMixin
 */
class StyleMixin {
    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the element's styles.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.StyleMixin
     * @readonly
     */
    get style() {
        let styleInstance = members._styleInstance.get(this);
        if (styleInstance) {
            return styleInstance;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const type = members._type.get(this);
        const StyleClass = styleClasses[type.compType] || extendedStyleClasses[type.compType];
        const compName = this.id;

        styleInstance = new StyleClass(RMI, compId, type, compName);
        members._styleInstance.set(this, styleInstance);
        return styleInstance;
    }
}

export default StyleMixin;
